import React, { useState, useEffect, useRef, useMemo } from 'react'
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Switch,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  InputAdornment,
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import CheckCircleOutlineIcon from 'components/material-symbol/icons/CheckCircleOutline'
import DeleteIcon from 'components/material-symbol/icons/Delete'
import ClearIcon from 'components/material-symbol/icons/Clear'
import PublishIcon from 'components/material-symbol/icons/Publish'
import ShareIcon from 'components/material-symbol/icons/Share'
import FileCopyIcon from 'components/material-symbol/icons/FileCopy'
import { useQuery, useMutation } from '@apollo/client'
import { useParams, useHistory, Prompt } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import debounce from 'lodash/debounce'
import { StatusChip, MediaUpload } from '../components'
import {
  GET_MY_HOLE_IN_ONE,
  UPDATE_MY_HOLE_IN_ONE,
  PUBLISH_MY_HOLE_IN_ONE,
  DELETE_MY_HOLE_IN_ONE,
} from './query'
import { placesQuery } from 'lib/search/places-client'
import { PGA_COM_HOST } from 'env'
import useStyles from './styles'
import ClubSelector from './ClubSelector'
import { isDetailAuthorizationError } from './auth-error'

const SECTIONS = [
  { id: 'sharing', label: 'Sharing' },
  { id: 'verified', label: 'Certified Details' },
  { id: 'about', label: 'About You' },
  { id: 'equipment', label: 'Equipment' },
  { id: 'witnesses', label: 'Witnesses' },
  { id: 'media', label: 'Media' },
  { id: 'privacy', label: 'Privacy' },
]

const DISPLAY_NAME_OPTIONS = [
  { value: 'FULL_NAME', label: 'Full Name' },
  { value: 'FIRST_NAME_LAST_INITIAL', label: 'First Name + Last Initial' },
  { value: 'ANONYMOUS', label: 'Anonymous' },
]

const MAX_WITNESSES = 4
const MAX_STORY_CHARACTERS = 500

const formatDisplayName = (fullName, format) => {
  if (!fullName || format === 'ANONYMOUS') return 'Anonymous'
  if (format === 'FULL_NAME') return fullName

  const parts = fullName.trim().split(/\s+/)
  if (parts.length === 1) return parts[0]
  const firstName = parts[0]
  const lastInitial = parts[parts.length - 1][0]
  return `${firstName} ${lastInitial}.`
}

const formatWitnessDisplayName = fullName => {
  if (!fullName) return ''
  const parts = fullName.trim().split(/\s+/)
  if (parts.length === 1) return parts[0]
  const firstName = parts[0]
  const lastInitial = parts[parts.length - 1][0]
  return `${firstName} ${lastInitial}.`
}

const mapPlacesResults = results => {
  return results.map(hit => ({
    label: `${hit.locale_names[0]}, ${hit.administrative[0]}`,
    value: `${hit.locale_names[0]}, ${hit.administrative[0]}`,
  }))
}

const CLUB_BRAND_TOP_10 = [
  'Ping',
  'Titleist',
  'Callaway',
  'TaylorMade',
  'Mizuno',
  'Cobra',
  'Cleveland',
  'PXG',
  'Srixon',
  'Wilson Staff',
]

const CLUB_BRAND_FULL_LIST = [
  ...CLUB_BRAND_TOP_10,
  'Nike',
  'Adams',
  'Ben Hogan',
  'Bridgestone',
  'Cleveland Golf',
  'MacGregor',
  'XXIO',
  'Other',
]

const BALL_BRAND_OPTIONS = [
  'Titleist',
  'Callaway',
  'TaylorMade',
  'Bridgestone',
  'Srixon',
  'Other',
]

const GENDER_OPTIONS = ['Female', 'Male', 'Non-Binary']
const formatDate = dateString => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  })
}

const AchievementsDetailPage = () => {
  const classes = useStyles()
  const { id } = useParams()
  const history = useHistory()
  const { enqueueSnackbar } = useSnackbar()
  const sectionRefs = useRef({})
  const [activeSection, setActiveSection] = useState('verified')
  const [formData, setFormData] = useState({
    golferName: '',
    golferHometown: '',
    gender: '',
    age: '',
    handicap: '',
    yearsPlaying: '',
    previousHios: '',
    publicVisibility: false,
    clubBrand: '',
    clubModel: '',
    clubType: '',
    ballBrand: '',
    ballModel: '',
    description: '',
    displayNameFormat: 'FULL_NAME',
    privateMode: false,
  })
  const [witnesses, setWitnesses] = useState([
    { id: 0, name: '' },
    { id: 1, name: '' },
    { id: 2, name: '' },
    { id: 3, name: '' },
  ])
  const [photos, setPhotos] = useState([])
  const [videos, setVideos] = useState([])
  const [hasChanges, setHasChanges] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showPublishConfirm, setShowPublishConfirm] = useState(false)
  const [showAllClubBrands, setShowAllClubBrands] = useState(false)
  const [hometownOptions, setHometownOptions] = useState([])
  const [hometownInputValue, setHometownInputValue] = useState('')
  const [linkCopied, setLinkCopied] = useState(false)
  const isInitialLoad = useRef(true)

  const { data, loading, error } = useQuery(GET_MY_HOLE_IN_ONE, {
    variables: { id },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'none',
    onError: queryError => {
      if (isDetailAuthorizationError(queryError)) {
        history.push('/achievements')
      }
    },
  })

  const [updateMyHoleInOne, { loading: saving }] = useMutation(
    UPDATE_MY_HOLE_IN_ONE,
    {
      onCompleted: () => {
        enqueueSnackbar('Changes saved successfully', { variant: 'success' })
        setHasChanges(false)
      },
      onError: err => {
        enqueueSnackbar(err.message || 'Failed to save changes', {
          variant: 'error',
        })
      },
    },
  )

  const [publishMyHoleInOne, { loading: publishing }] = useMutation(
    PUBLISH_MY_HOLE_IN_ONE,
    {
      onCompleted: () => {
        enqueueSnackbar('Achievement submitted for review!', {
          variant: 'success',
        })
        setShowPublishConfirm(false)
      },
      onError: err => {
        enqueueSnackbar(err.message || 'Failed to publish', {
          variant: 'error',
        })
        setShowPublishConfirm(false)
      },
      refetchQueries: [{ query: GET_MY_HOLE_IN_ONE, variables: { id } }],
    },
  )

  const [deleteMyHoleInOne, { loading: deleting }] = useMutation(
    DELETE_MY_HOLE_IN_ONE,
    {
      onCompleted: response => {
        if (response?.deleteMyHoleInOne?.success) {
          enqueueSnackbar('Hole-in-one record deleted successfully', {
            variant: 'success',
          })
          setShowDeleteConfirm(false)
          history.push('/achievements')
        } else {
          enqueueSnackbar(
            response?.deleteMyHoleInOne?.message || 'Failed to delete record',
            { variant: 'error' },
          )
          setShowDeleteConfirm(false)
        }
      },
      onError: err => {
        enqueueSnackbar(err.message || 'Failed to delete record', {
          variant: 'error',
        })
        setShowDeleteConfirm(false)
      },
    },
  )

  // Debounced hometown search
  const searchHometown = useMemo(
    () =>
      debounce(async query => {
        if (!query || query.length < 2) {
          setHometownOptions([])
          return
        }
        try {
          const results = await placesQuery(query)
          setHometownOptions(mapPlacesResults(results.hits))
        } catch (error) {
          console.error('Error searching places:', error)
          setHometownOptions([])
        }
      }, 300),
    [],
  )

  useEffect(() => {
    if (data?.myHoleInOne) {
      const hio = data.myHoleInOne

      setFormData({
        golferName: hio.golferName || '',
        golferHometown: hio.golferHometown || '',
        gender: hio.gender || '',
        age: hio.age?.toString() || '',
        handicap: hio.handicap?.toString() || '',
        yearsPlaying: hio.yearsPlaying?.toString() || '',
        previousHios: hio.totalHios ? (hio.totalHios - 1).toString() : '0',
        publicVisibility: hio.publicVisibility || false,
        clubBrand: hio.clubBrand || '',
        clubModel: hio.clubModel || '',
        clubType: hio.clubType || '',
        ballBrand: hio.ballBrand || '',
        ballModel: hio.ballModel || '',
        description: hio.description || '',
        displayNameFormat: hio.displayNameFormat || 'FULL_NAME',
        privateMode: !hio.publicVisibility,
      })
      setHometownInputValue(hio.golferHometown || '')
      const existingWitnesses =
        hio.witnesses && Array.isArray(hio.witnesses)
          ? hio.witnesses.slice(0, MAX_WITNESSES)
          : []
      const newWitnesses = Array.from({ length: MAX_WITNESSES }, (_, idx) => ({
        id: idx,
        name: existingWitnesses[idx]
          ? typeof existingWitnesses[idx] === 'string'
            ? existingWitnesses[idx]
            : existingWitnesses[idx].name || ''
          : '',
      }))
      setWitnesses(newWitnesses)
      if (hio.photoUrls) {
        setPhotos(
          hio.photoUrls.map((url, idx) => ({ id: idx, url, isNew: false })),
        )
      }
      if (hio.videoUrls) {
        setVideos(
          hio.videoUrls.map((url, idx) => ({
            id: `video-${idx}`,
            url,
            isNew: false,
          })),
        )
      }

      if (hio.status === 'PUBLISHED') {
        setActiveSection('sharing')
      } else {
        setActiveSection('verified')
      }

      // Only reset hasChanges on initial load to avoid overwriting user's unsaved changes
      // during background refetches
      if (isInitialLoad.current) {
        setHasChanges(false)
        isInitialLoad.current = false
      }
    }
  }, [data])

  // Warn user about unsaved changes when closing tab/window
  useEffect(() => {
    const handleBeforeUnload = e => {
      if (hasChanges) {
        e.preventDefault()
        e.returnValue = ''
        return ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [hasChanges])

  const handleFieldChange = (field, value) => {
    setFormData(prev => {
      const updates = { [field]: value }

      if (field === 'clubType' && !value) {
        updates.clubBrand = ''
        updates.clubModel = ''
      }
      if (field === 'clubBrand' && !value) {
        updates.clubModel = ''
      }
      if (field === 'ballBrand' && !value) {
        updates.ballModel = ''
      }

      return {
        ...prev,
        ...updates,
      }
    })
    setHasChanges(true)
  }

  const handleClubChange = clubTypeValue => {
    setFormData(prev => ({
      ...prev,
      clubType: clubTypeValue,
      // Clear brand and model when club type changes
      clubBrand: prev.clubType !== clubTypeValue ? '' : prev.clubBrand,
      clubModel: prev.clubType !== clubTypeValue ? '' : prev.clubModel,
    }))
    setHasChanges(true)
  }

  const handleWitnessChange = (index, value) => {
    setWitnesses(prev =>
      prev.map((w, idx) => (idx === index ? { ...w, name: value } : w)),
    )
    setHasChanges(true)
  }

  const handleClearWitness = index => {
    setWitnesses(prev =>
      prev.map((w, idx) => (idx === index ? { ...w, name: '' } : w)),
    )
    setHasChanges(true)
  }

  const handlePhotosChange = newPhotos => {
    setPhotos(newPhotos)
    setHasChanges(true)
  }

  const handleVideosChange = newVideos => {
    setVideos(newVideos)
    setHasChanges(true)
  }

  const handleDeleteRecord = () => {
    deleteMyHoleInOne({
      variables: { id },
    })
  }

  const handlePublish = () => {
    publishMyHoleInOne({
      variables: { id },
    })
  }

  const isEditable = true
  const isDraft = data?.myHoleInOne?.status === 'DRAFT'
  const isPublished = data?.myHoleInOne?.status === 'PUBLISHED'

  const handleSave = () => {
    // Prepare witnesses as an array of names
    const witnessNames = witnesses
      .filter(w => w.name?.trim())
      .map(w => w.name.trim())

    const photoUrls = photos.map(p => p.url)
    const videoUrls = videos.map(v => v.url)

    const input = {
      id,
      golferName: formData.golferName || null,
      golferHometown: formData.golferHometown || null,
      gender: formData.gender || null,
      age: formData.age ? parseInt(formData.age, 10) : null,
      handicap: formData.handicap ? parseFloat(formData.handicap) : null,
      yearsPlaying: formData.yearsPlaying
        ? parseInt(formData.yearsPlaying, 10)
        : null,
      totalHios: formData.previousHios
        ? parseInt(formData.previousHios, 10) + 1
        : 1,
      clubType: formData.clubType || null,
      clubBrand: formData.clubBrand || null,
      clubModel: formData.clubModel || null,
      ballBrand: formData.ballBrand || null,
      ballModel: formData.ballModel || null,
      witnesses: witnessNames.length > 0 ? witnessNames : [],
      description: formData.description,
      photoUrls: photoUrls,
      videoUrls: videoUrls,
      displayNameFormat: formData.displayNameFormat,
      publicVisibility: !formData.privateMode,
    }

    updateMyHoleInOne({
      variables: { input },
    })
  }

  const handleCancel = () => {
    history.push('/achievements')
  }

  const scrollToSection = sectionId => {
    setActiveSection(sectionId)
    const element = sectionRefs.current[sectionId]
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const getShareUrl = () => {
    const pgaComHost = PGA_COM_HOST || 'https://www.pga.com'
    return `${pgaComHost}/aces/${achievement.slug}`
  }

  const handleCopyLink = () => {
    const shareUrl = getShareUrl()
    navigator.clipboard
      .writeText(shareUrl)
      .then(() => {
        setLinkCopied(true)
        enqueueSnackbar('Link copied to clipboard!', { variant: 'success' })
        setTimeout(() => setLinkCopied(false), 3000)
      })
      .catch(() => {
        enqueueSnackbar('Failed to copy link', { variant: 'error' })
      })
  }

  const handleShareTwitter = () => {
    const shareUrl = getShareUrl()
    const text = `I just recorded a hole-in-one at ${achievement.courseName}! Check it out on PGA.com`
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        text,
      )}&url=${encodeURIComponent(shareUrl)}`,
      '_blank',
    )
  }

  const handleShareFacebook = () => {
    const shareUrl = getShareUrl()
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        shareUrl,
      )}`,
      '_blank',
    )
  }

  const handleShareReddit = () => {
    const shareUrl = getShareUrl()
    const title = `Hole-in-One at ${achievement.courseName}`
    window.open(
      `https://www.reddit.com/r/golf/submit?url=${encodeURIComponent(
        shareUrl,
      )}&title=${encodeURIComponent(title)}`,
      '_blank',
    )
  }

  if (loading && !data) {
    return (
      <Container maxWidth="lg" className={classes.container}>
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  if (error) {
    return (
      <Container maxWidth="lg" className={classes.container}>
        <Box className={classes.errorContainer}>
          <Typography className={classes.errorTitle}>
            Error loading achievement
          </Typography>
          <Typography className={classes.errorText}>
            {error.message || 'Please try again later.'}
          </Typography>
          <Button
            className={classes.backButton}
            onClick={() => history.push('/achievements')}
          >
            Back to Achievements
          </Button>
        </Box>
      </Container>
    )
  }

  const achievement = data?.myHoleInOne

  if (!achievement) {
    return (
      <Container maxWidth="lg" className={classes.container}>
        <Box className={classes.errorContainer}>
          <Typography className={classes.errorTitle}>
            Achievement not found
          </Typography>
          <Typography className={classes.errorText}>
            The achievement you're looking for doesn't exist.
          </Typography>
          <Button
            className={classes.backButton}
            onClick={() => history.push('/achievements')}
          >
            Back to Achievements
          </Button>
        </Box>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg" className={classes.container}>
      <Prompt
        when={hasChanges}
        message="You have unsaved changes. Are you sure you want to leave this page?"
      />
      <Box className={classes.root}>
        <Box className={classes.header}>
          <Box className={classes.headerLeft}>
            <Box className={classes.titleRow}>
              <Typography className={classes.title}>
                Hole-in-One Details
              </Typography>
              {achievement?.status && (
                <StatusChip status={achievement.status} />
              )}
            </Box>
            <Typography className={classes.subtitle}>
              Review and edit the details of your recorded ace.
            </Typography>
          </Box>
          <Box className={classes.headerButtons}>
            <Button className={classes.cancelButton} onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              className={classes.saveButton}
              onClick={handleSave}
              disabled={!hasChanges || saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            {isDraft && (
              <Button
                className={classes.publishButton}
                onClick={() => setShowPublishConfirm(true)}
                disabled={publishing}
                startIcon={<PublishIcon />}
              >
                {publishing ? 'Publishing...' : 'Publish'}
              </Button>
            )}
          </Box>
        </Box>

        <Box className={classes.contentContainer}>
          <Box className={classes.sidebar}>
            <Box className={classes.sidebarNav}>
              {SECTIONS.filter(
                section => section.id !== 'sharing' || isPublished,
              ).map(section => (
                <Typography
                  key={section.id}
                  className={`${classes.sidebarItem} ${
                    activeSection === section.id
                      ? classes.sidebarItemActive
                      : ''
                  }`}
                  onClick={() => scrollToSection(section.id)}
                >
                  {section.label}
                </Typography>
              ))}
            </Box>
          </Box>

          <Box className={classes.mainContent}>
            {isPublished && (
              <Box
                className={`${classes.section} ${classes.sharingSection}`}
                ref={el => (sectionRefs.current.sharing = el)}
              >
                <Box className={classes.sectionHeader}>
                  <ShareIcon
                    className={classes.sectionHeaderIcon}
                    style={{ color: '#003087' }}
                  />
                  <Box className={classes.sectionHeaderContent}>
                    <Typography className={classes.sectionTitle}>
                      Share Your Achievement
                    </Typography>
                    <Typography className={classes.sectionSubtitle}>
                      Spread the word about your hole-in-one
                    </Typography>
                  </Box>
                </Box>
                <Box className={classes.sectionContent}>
                  <Typography className={classes.shareDescription}>
                    Share your certified hole-in-one with friends, family, and
                    fellow golfers
                  </Typography>

                  <Box className={classes.shareButtonsGrid}>
                    <Button
                      className={classes.shareButton}
                      startIcon={
                        <img
                          height="24"
                          width="24"
                          alt="X"
                          src="https://cdn.simpleicons.org/x/000000"
                        />
                      }
                      onClick={handleShareTwitter}
                    >
                      Post on X
                    </Button>
                    <Button
                      className={classes.shareButton}
                      startIcon={
                        <img
                          height="24"
                          width="24"
                          alt="Facebook"
                          src="https://cdn.simpleicons.org/facebook/0866FF"
                        />
                      }
                      onClick={handleShareFacebook}
                    >
                      Share on Facebook
                    </Button>
                    <Button
                      className={classes.shareButton}
                      startIcon={
                        <img
                          height="24"
                          width="24"
                          alt="Reddit"
                          src="https://cdn.simpleicons.org/reddit/FF4500"
                        />
                      }
                      onClick={handleShareReddit}
                    >
                      Post on r/golf
                    </Button>
                  </Box>

                  <Box className={classes.shareLinkContainer}>
                    <Box className={classes.shareLinkBox}>
                      <Typography className={classes.shareLinkUrl}>
                        {getShareUrl()}
                      </Typography>
                      <Button
                        className={classes.copyLinkButton}
                        startIcon={<FileCopyIcon />}
                        onClick={handleCopyLink}
                      >
                        {linkCopied ? 'Copied!' : 'Copy Link'}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}

            <Box
              className={classes.section}
              ref={el => (sectionRefs.current.verified = el)}
            >
              <Box className={classes.sectionHeader}>
                <CheckCircleOutlineIcon className={classes.sectionHeaderIcon} />
                <Box className={classes.sectionHeaderContent}>
                  <Typography className={classes.sectionTitle}>
                    Certified Details
                  </Typography>
                  <Typography className={classes.sectionSubtitle}>
                    These details have been certified by a PGA Professional and
                    cannot be edited.
                  </Typography>
                </Box>
              </Box>
              <Box className={classes.sectionContent}>
                <Box className={classes.verifiedGrid}>
                  <Box className={classes.fieldGroup}>
                    <Typography className={classes.fieldLabel}>
                      Golf Course
                    </Typography>
                    <Typography className={classes.fieldValue}>
                      {achievement.courseName}
                    </Typography>
                  </Box>
                  <Box className={classes.fieldGroup}>
                    <Typography className={classes.fieldLabel}>Date</Typography>
                    <Typography className={classes.fieldValue}>
                      {formatDate(achievement.occurredAt)}
                    </Typography>
                  </Box>
                  <Box className={classes.fieldGroup}>
                    <Typography className={classes.fieldLabel}>Hole</Typography>
                    <Typography className={classes.fieldValue}>
                      #{achievement.holeNumber}
                    </Typography>
                  </Box>
                  <Box className={classes.fieldGroup}>
                    <Typography className={classes.fieldLabel}>Par</Typography>
                    <Typography className={classes.fieldValue}>
                      {achievement.holePar}
                    </Typography>
                  </Box>
                  <Box className={classes.fieldGroup}>
                    <Typography className={classes.fieldLabel}>
                      Distance
                    </Typography>
                    <Typography className={classes.fieldValue}>
                      {achievement.yardage} Yds
                    </Typography>
                  </Box>
                  <Box className={classes.fieldGroup}>
                    <Typography className={classes.fieldLabel}>
                      Certified By
                    </Typography>
                    <Typography className={classes.fieldValue}>
                      {achievement.pgaProfessional || 'PGA Professional'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box
              className={classes.section}
              ref={el => (sectionRefs.current.about = el)}
            >
              <Box className={classes.sectionHeader}>
                <Box className={classes.sectionHeaderContent}>
                  <Typography className={classes.sectionTitle}>
                    About You
                  </Typography>
                </Box>
              </Box>
              <Box className={classes.sectionContent}>
                <Box className={classes.formGridNameAge}>
                  <TextField
                    label="Full Name"
                    variant="outlined"
                    className={classes.textField}
                    value={formData.golferName}
                    onChange={e =>
                      handleFieldChange('golferName', e.target.value)
                    }
                    disabled={!isEditable}
                  />
                  <TextField
                    label="Age"
                    variant="outlined"
                    type="number"
                    className={classes.textField}
                    value={formData.age}
                    onChange={e => handleFieldChange('age', e.target.value)}
                    inputProps={{ min: 1, max: 120 }}
                    disabled={!isEditable}
                  />
                </Box>
                <Box className={classes.formGrid} style={{ marginTop: 24 }}>
                  <Autocomplete
                    freeSolo
                    options={hometownOptions}
                    getOptionLabel={option =>
                      typeof option === 'string' ? option : option.label || ''
                    }
                    value={formData.golferHometown}
                    inputValue={hometownInputValue}
                    onInputChange={(_, newValue) => {
                      setHometownInputValue(newValue)
                      searchHometown(newValue)
                    }}
                    onChange={(_, newValue) => {
                      const value =
                        typeof newValue === 'string'
                          ? newValue
                          : newValue?.value || ''
                      handleFieldChange('golferHometown', value)
                    }}
                    disabled={!isEditable}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Hometown"
                        variant="outlined"
                        className={classes.textField}
                        placeholder="Start typing city name..."
                      />
                    )}
                  />
                  <Autocomplete
                    freeSolo
                    options={GENDER_OPTIONS}
                    value={formData.gender}
                    onChange={(_, newValue) => {
                      handleFieldChange('gender', newValue || '')
                    }}
                    onInputChange={(_, newValue, reason) => {
                      // Only trigger change when user types, not on mount/reset
                      if (reason === 'input') {
                        handleFieldChange('gender', newValue || '')
                      }
                    }}
                    disabled={!isEditable}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Gender"
                        variant="outlined"
                        className={classes.textField}
                        placeholder="Select or type"
                      />
                    )}
                  />
                </Box>
                <Box
                  className={classes.formGridThree}
                  style={{ marginTop: 24 }}
                >
                  <TextField
                    label="Handicap"
                    variant="outlined"
                    type="number"
                    className={classes.textField}
                    value={formData.handicap}
                    onChange={e =>
                      handleFieldChange('handicap', e.target.value)
                    }
                    inputProps={{ step: '0.1', min: -10, max: 54 }}
                    disabled={!isEditable}
                  />
                  <TextField
                    label="Years Playing Golf"
                    variant="outlined"
                    type="number"
                    className={classes.textField}
                    value={formData.yearsPlaying}
                    onChange={e =>
                      handleFieldChange('yearsPlaying', e.target.value)
                    }
                    inputProps={{ min: 0 }}
                    disabled={!isEditable}
                  />
                  <TextField
                    label="Total Hole in Ones"
                    variant="outlined"
                    type="number"
                    className={classes.textField}
                    value={formData.previousHios}
                    onChange={e =>
                      handleFieldChange('previousHios', e.target.value)
                    }
                    inputProps={{ min: 0 }}
                    disabled={!isEditable}
                  />
                </Box>
              </Box>
            </Box>

            <Box
              className={classes.section}
              ref={el => (sectionRefs.current.equipment = el)}
            >
              <Box className={classes.sectionHeader}>
                <Box className={classes.sectionHeaderContent}>
                  <Typography className={classes.sectionTitle}>
                    Equipment
                  </Typography>
                </Box>
              </Box>
              <Box className={classes.sectionContent}>
                <ClubSelector
                  value={formData.clubType}
                  onChange={handleClubChange}
                  disabled={!isEditable}
                />
                <Box className={classes.formGrid} style={{ marginTop: 24 }}>
                  <FormControl
                    variant="outlined"
                    className={classes.select}
                    disabled={!isEditable || !formData.clubType}
                  >
                    <InputLabel>Club Brand</InputLabel>
                    <Select
                      value={formData.clubBrand}
                      onChange={e =>
                        handleFieldChange('clubBrand', e.target.value)
                      }
                      label="Club Brand"
                    >
                      <MenuItem value="">
                        <em>Select</em>
                      </MenuItem>
                      {(showAllClubBrands
                        ? CLUB_BRAND_FULL_LIST
                        : CLUB_BRAND_TOP_10
                      ).map(brand => (
                        <MenuItem key={brand} value={brand}>
                          {brand}
                        </MenuItem>
                      ))}
                      {!showAllClubBrands && (
                        <MenuItem
                          value=""
                          onMouseDown={e => {
                            e.preventDefault()
                            e.stopPropagation()
                            setShowAllClubBrands(true)
                          }}
                          style={{
                            fontWeight: 600,
                            color: '#003087',
                          }}
                        >
                          Show All Brands...
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Club Model"
                    variant="outlined"
                    className={classes.textField}
                    value={formData.clubModel}
                    onChange={e =>
                      handleFieldChange('clubModel', e.target.value)
                    }
                    placeholder="e.g., Vokey SM9"
                    disabled={!isEditable || !formData.clubBrand}
                  />
                </Box>
                <Box className={classes.formGrid} style={{ marginTop: 24 }}>
                  <FormControl
                    variant="outlined"
                    className={classes.select}
                    disabled={!isEditable}
                  >
                    <InputLabel>Ball Brand</InputLabel>
                    <Select
                      value={formData.ballBrand}
                      onChange={e =>
                        handleFieldChange('ballBrand', e.target.value)
                      }
                      label="Ball Brand"
                    >
                      <MenuItem value="">
                        <em>Select</em>
                      </MenuItem>
                      {BALL_BRAND_OPTIONS.map(brand => (
                        <MenuItem key={brand} value={brand}>
                          {brand}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Ball Model"
                    variant="outlined"
                    className={classes.textField}
                    value={formData.ballModel}
                    onChange={e =>
                      handleFieldChange('ballModel', e.target.value)
                    }
                    placeholder="e.g., Pro V1"
                    disabled={!isEditable || !formData.ballBrand}
                  />
                </Box>
              </Box>
            </Box>

            <Box
              className={classes.section}
              ref={el => (sectionRefs.current.witnesses = el)}
            >
              <Box className={classes.sectionHeader}>
                <Box className={classes.sectionHeaderContent}>
                  <Typography className={classes.sectionTitle}>
                    Witnesses
                  </Typography>
                  <Typography className={classes.sectionSubtitle}>
                    Who was there to see it? Names will be displayed as First
                    Name + Last Initial.
                  </Typography>
                </Box>
              </Box>
              <Box className={classes.sectionContent}>
                <Box className={classes.witnessesGrid}>
                  {witnesses.map((witness, index) => (
                    <Box
                      key={witness.id}
                      className={classes.witnessFieldWrapper}
                    >
                      <TextField
                        label="Witness Name"
                        variant="outlined"
                        fullWidth
                        className={classes.textField}
                        value={witness.name}
                        onChange={e =>
                          handleWitnessChange(index, e.target.value)
                        }
                        disabled={!isEditable}
                        InputProps={{
                          endAdornment: witness.name && isEditable && (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="clear witness name"
                                onClick={() => handleClearWitness(index)}
                                edge="end"
                                size="small"
                                className={classes.clearWitnessButton}
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      {witness.name && (
                        <Typography className={classes.witnessDisplayAs}>
                          Will display as:{' '}
                          <strong>
                            {formatWitnessDisplayName(witness.name)}
                          </strong>
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            <Box
              className={classes.section}
              ref={el => (sectionRefs.current.media = el)}
            >
              <Box className={classes.sectionHeader}>
                <Box className={classes.sectionHeaderContent}>
                  <Typography className={classes.sectionTitle}>
                    Tell the Story
                  </Typography>
                </Box>
              </Box>
              <Box className={classes.sectionContent}>
                <Box className={classes.storyContainer}>
                  <TextField
                    multiline
                    minRows={6}
                    variant="outlined"
                    fullWidth
                    className={classes.storyTextArea}
                    value={formData.description}
                    onChange={e => {
                      const value = e.target.value
                      if (value.length <= MAX_STORY_CHARACTERS) {
                        handleFieldChange('description', value)
                      }
                    }}
                    placeholder="Describe the shot, the weather, your reaction..."
                    inputProps={{ maxLength: MAX_STORY_CHARACTERS }}
                    disabled={!isEditable}
                    error={formData.description.length >= MAX_STORY_CHARACTERS}
                  />
                  <Box
                    className={`${classes.characterCount} ${
                      formData.description.length >= MAX_STORY_CHARACTERS
                        ? classes.characterCountError
                        : ''
                    }`}
                  >
                    {formData.description.length}/{MAX_STORY_CHARACTERS}{' '}
                    characters
                    {formData.description.length >= MAX_STORY_CHARACTERS && (
                      <span className={classes.characterLimitReached}>
                        {' '}
                        - Character limit reached
                      </span>
                    )}
                  </Box>
                </Box>

                <MediaUpload
                  photos={photos}
                  videos={videos}
                  onPhotosChange={handlePhotosChange}
                  onVideosChange={handleVideosChange}
                  disabled={!isEditable}
                  maxPhotos={10}
                  maxVideos={3}
                />
              </Box>
            </Box>

            <Box
              className={classes.section}
              ref={el => (sectionRefs.current.privacy = el)}
            >
              <Box className={classes.sectionHeader}>
                <Box className={classes.sectionHeaderContent}>
                  <Typography className={classes.sectionTitle}>
                    Privacy Settings
                  </Typography>
                </Box>
              </Box>
              <Box className={classes.sectionContent}>
                <Box className={classes.privacyField}>
                  <Typography className={classes.privacyFieldLabel}>
                    Display Name Format
                  </Typography>
                  <FormControl
                    variant="outlined"
                    fullWidth
                    className={classes.select}
                    disabled={!isEditable}
                  >
                    <Select
                      value={formData.displayNameFormat}
                      onChange={e =>
                        handleFieldChange('displayNameFormat', e.target.value)
                      }
                    >
                      {DISPLAY_NAME_OPTIONS.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Box className={classes.displayNamePreview}>
                    <Typography className={classes.displayNamePreviewLabel}>
                      Preview:
                    </Typography>
                    <Typography className={classes.displayNamePreviewValue}>
                      {formatDisplayName(
                        formData.golferName,
                        formData.displayNameFormat,
                      )}
                    </Typography>
                  </Box>
                  <Typography className={classes.privacyFieldHelper}>
                    Choose how your name appears on public leaderboards.
                  </Typography>
                </Box>

                <Box className={classes.privacyToggleRow}>
                  <Box>
                    <Typography className={classes.privacyFieldLabel}>
                      Private Mode
                    </Typography>
                    <Typography className={classes.privacyFieldHelper}>
                      When enabled, this achievement will not appear in club
                      feeds or public leaderboards.
                    </Typography>
                  </Box>
                  <Switch
                    checked={formData.privateMode}
                    onChange={e =>
                      handleFieldChange('privateMode', e.target.checked)
                    }
                    color="primary"
                    disabled={!isEditable}
                  />
                </Box>

                <Box className={classes.deleteSection}>
                  <Typography className={classes.deleteTitle}>
                    Delete Record
                  </Typography>
                  <Box className={classes.deleteWarning}>
                    <Box className={classes.deleteWarningContent}>
                      <Typography className={classes.deleteWarningTitle}>
                        Warning: This action cannot be undone.
                      </Typography>
                      <Typography className={classes.deleteWarningText}>
                        Permanently delete this hole-in-one record and remove it
                        from your profile stats.
                      </Typography>
                    </Box>
                    <Button
                      className={classes.deleteButton}
                      onClick={() => setShowDeleteConfirm(true)}
                    >
                      Delete Record
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={showDeleteConfirm}
        onClose={() => !deleting && setShowDeleteConfirm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle disableTypography className={classes.deleteDialogTitle}>
          <DeleteIcon className={classes.deleteDialogIcon} />
          <Typography variant="h6" component="span">
            Delete Hole-in-One Record
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography className={classes.deleteDialogText}>
            Are you sure you want to permanently delete this hole-in-one record?
          </Typography>
          <Box className={classes.deleteDialogWarning}>
            <Typography className={classes.deleteDialogWarningTitle}>
              This action cannot be undone:
            </Typography>
            <ul className={classes.deleteDialogList}>
              <li>Your hole-in-one record will be permanently deleted</li>
              <li>It will be removed from your profile and achievements</li>
              <li>Any associated photos and story will be lost</li>
              <li>This record cannot be recovered after deletion</li>
            </ul>
          </Box>
        </DialogContent>
        <DialogActions className={classes.deleteDialogActions}>
          <Button
            onClick={() => setShowDeleteConfirm(false)}
            className={classes.deleteDialogCancelButton}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteRecord}
            className={classes.deleteDialogConfirmButton}
            disabled={deleting}
            startIcon={
              deleting ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {deleting ? 'Deleting...' : 'Delete Permanently'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showPublishConfirm}
        onClose={() => setShowPublishConfirm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle disableTypography className={classes.publishDialogTitle}>
          <PublishIcon className={classes.publishDialogIcon} />
          <Typography variant="h6" component="span">
            Publish Your Achievement
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography className={classes.publishDialogText}>
            Once published, your hole-in-one will be submitted for review. After
            approval, it will appear on public leaderboards and your achievement
            page.
          </Typography>
          <Box className={classes.publishDialogInfo}>
            <Typography className={classes.publishDialogInfoTitle}>
              What happens next:
            </Typography>
            <ul className={classes.publishDialogList}>
              <li>Your submission will be reviewed by our team</li>
              <li>Once approved, your achievement becomes publicly visible</li>
              <li>
                You can continue to edit the details even after publishing
              </li>
              <li>
                Your name will display as:{' '}
                <strong>
                  {formatDisplayName(
                    formData.golferName,
                    formData.displayNameFormat,
                  )}
                </strong>
              </li>
            </ul>
          </Box>
        </DialogContent>
        <DialogActions className={classes.publishDialogActions}>
          <Button
            onClick={() => setShowPublishConfirm(false)}
            className={classes.publishDialogCancelButton}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePublish}
            className={classes.publishDialogConfirmButton}
            disabled={publishing}
            startIcon={
              publishing ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {publishing ? 'Publishing...' : 'Confirm & Publish'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

export default AchievementsDetailPage
